import { useNavigate } from 'react-router-dom';
import { Button, ButtonToolbar, Divider, Progress, Stack } from 'rsuite';

import { TbProgress } from 'react-icons/tb';
import { TbProgressX } from 'react-icons/tb';

import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  globalApi,
  regionApi,
  useAppDispatch,
  useAppSelector,
  useGetStatusQuery
} from '@monorepo/shared';
import { selectRegions } from '@monorepo/shared/store/authSlice';
import CheckOutlineIcon from '@rsuite/icons/CheckOutline';

interface TaskObj {
  sn?: string;
  task_id: string;
  account_id: string;
  region_alias: string;
  is_trial?: boolean;
  callBack?: any;
}
const CheckBtn = (data: Record<string, any>) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('active');
  useEffect(() => {
    if (data.data?.status === 'FAILURE') {
      setStatus('fail');
    }
    if (data.data?.status === 'PENDING') {
      setStatus('active');
    }
    if (data.data?.status === 'STARTED') {
      setStatus('active');
    }
    if (data.data?.stage === 'DONE') {
      if (data.arg?.setIsCheck) {
        data.arg.setIsCheck(false);
      }
      setStatus('done');
    }
  }, [data]);
  const goToProvisionProgress = () => {
    navigate(`provision/${data.data.arg?.sn}/${data.data.arg?.region_alias}`);
  };
  return status === 'done' ? (
    <CheckOutlineIcon color="green" />
  ) : (
    <Button
      style={{ width: '100px' }}
      size="xs"
      appearance="ghost"
      color={status === 'fail' ? 'red' : undefined}
      title={`Provision ${data?.data?.status}`}
      onClick={goToProvisionProgress}
      startIcon={
        status === 'active' ? (
          <TbProgress color="var(--fc-blue)" className="icon-spinner" />
        ) : (
          <TbProgressX color="red" />
        )
      }
    >
      Provision...
    </Button>
  );
};

const SocProgressDetail = (data: Record<string, any>) => {
  const [socData, setSocData] = useState<Record<string, any>[]>([]);
  const [totalData, setTotalData] = useState<Record<string, any>>({});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getBarStatus = (item: Record<string, any>) => {
    if (item?.stage === 'DONE') {
      return 'success';
    } else if (item?.status === 'FAILURE') {
      return 'fail';
    } else {
      return 'active';
    }
  };

  const getBarPercent = (item: Record<string, any>) => {
    const time_consumed = item?.time_consumed || 0;
    const eta_pool = item?.eta_pool || 20 * 60;
    const eta_no_pool = item?.eta_no_pool || 40 * 60;
    if (item?.stage === 'DONE' || item?.status === 'FAILURE') {
      return 100;
    } else if (time_consumed >= eta_pool) {
      return Math.floor((time_consumed / eta_no_pool) * 100);
    } else {
      return Math.floor((time_consumed / eta_pool) * 100);
    }
  };

  const getDisplayStage = (s: string): string => {
    let str = s || '';
    if (s === 'DONE') {
      str = 'Complete';
    } else if (s === 'NOT_DONE') {
      str = 'Failed';
    }
    return str
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getLeftTime = (item: Record<string, any>) => {
    const time_consumed = item?.time_consumed || 0;
    const eta_pool = item?.eta_pool || 20 * 60;
    const eta_no_pool = item?.eta_no_pool || 40 * 60;
    if (item?.stage === 'DONE' || item.status === 'FAILURE') {
      return 0;
    } else if (time_consumed >= eta_no_pool) {
      return 0;
    } else if (time_consumed >= eta_pool) {
      return Math.ceil((eta_no_pool - time_consumed) / 60);
    } else {
      return Math.ceil((eta_pool - time_consumed) / 60);
    }
  };
  const getTotalStatus = (
    items: Record<string, any>[],
    over_all: Record<string, any>
  ) => {
    if (!items.length) {
      if (over_all?.status === 'FAILURE') {
        return 'fail';
      } else {
        return over_all?.status?.toLowerCase();
      }
    } else if (items.some((o) => o?.bar_status === 'fail')) {
      return 'fail';
    } else if (items.every((o) => o?.bar_status === 'success')) {
      return 'success';
    } else {
      return 'active';
    }
  };
  const getTotalPercent = (
    items: Record<string, any>[],
    totalStatus: string
  ) => {
    if (totalStatus === 'success' || totalStatus === 'fail') {
      return 100;
    }
    return items.length
      ? Math.floor(
          items?.reduce((sum, item) => sum + item?.bar_percent || 0, 0) /
            items?.length
        )
      : 0;
  };

  const getTotalLeftTime = (
    items: Record<string, any>[],
    totalStatus: string
  ) => {
    if (totalStatus === 'pending') {
      return -1;
    } else if (totalStatus === 'success' || totalStatus === 'fail') {
      return 0;
    }
    return items.length ? Math.max(...items?.map((p) => p?.left_time || 0)) : 0;
  };

  const getTotalErrorList = (
    items: Record<string, any>[],
    totalStatus: string,
    over_all: Record<string, any>
  ) => {
    if (totalStatus === 'fail') {
      return items?.length
        ? items
            ?.filter((o: Record<string, any>) => o.error_code)
            .map((o: Record<string, any>) => ({
              module: o.module,
              error_code: o.error_code,
              error_desc: o.error_desc
            }))
        : [{ ...over_all }];
    }
    return [];
  };

  useEffect(() => {
    if (data?.data) {
      const arr: Record<string, any>[] = Object?.values(data.data);
      const formatArr = arr?.map((o: Record<string, any>) => ({
        ...o,
        stage: o?.stage
          ? o.stage
          : o?.status === 'PENDING'
          ? o.status
          : 'Unknown',
        bar_status: getBarStatus(o),
        bar_percent: getBarPercent(o),
        left_time: getLeftTime(o)
      }));
      setSocData(formatArr);
      const total_status = getTotalStatus(formatArr, data?.over_all);
      const total_percent = getTotalPercent(formatArr, total_status);
      const total_left_time = getTotalLeftTime(formatArr, total_status);
      const total_error_list = getTotalErrorList(
        formatArr,
        total_status,
        data?.over_all
      );
      setTotalData({
        bar_status: total_status,
        bar_percent: total_percent,
        left_time: total_left_time,
        error_list: total_error_list
      });
    }
  }, [data]);

  const getServiceDetail = (
    o: Record<string, any>,
    i: number,
    isHideTime: boolean = false
  ) => {
    const barStatus = o?.bar_status;
    const barPercent = o?.bar_percent;
    const leftTime = o?.left_time;
    return (
      <div key={i} style={{ marginBottom: '20px' }}>
        <div
          style={{
            color: '#B0B0B0',
            fontSize: '16px',
            fontWeight: 700,
            marginBottom: '8px'
          }}
        >
          {o.module || 'Provisioning Progress'}
        </div>
        <Stack>
          <Stack.Item
            basis={o?.stage ? '15%' : '0%'}
            style={{
              display: `${getDisplayStage(o?.stage) ? 'block' : 'none'}`,
              fontSize: '0.85em',
              minWidth: '100px',
              color: `${
                o?.stage === 'DONE'
                  ? 'var(--rs-progress-bar-success)'
                  : o?.stage === 'NOT_DONE'
                  ? 'var(--rs-progress-bar-fail)'
                  : 'var(--rs-progress-bar)'
              }`
            }}
          >
            {getDisplayStage(o?.stage)}
          </Stack.Item>
          <Stack.Item flex={1}>
            <Progress.Line
              percent={barPercent}
              status={barStatus}
              showInfo={false}
              // strokeWidth={2}
              trailColor="#343953"
            ></Progress.Line>
          </Stack.Item>
          <Stack.Item
            basis={'15%'}
            style={{
              color: 'var(--fsr-space-footer-text)',
              fontSize: '0.85em',
              minWidth: '120px'
            }}
          >
            {leftTime && !isHideTime ? (
              leftTime > 0 ? (
                ` ${leftTime}  min remaining`
              ) : (
                'Pending...'
              )
            ) : (
              <></>
            )}
          </Stack.Item>
        </Stack>
      </div>
    );
  };

  return (
    <>
      <div>{getServiceDetail(totalData, 0)}</div>
      {totalData?.error_list?.length ? (
        <ul>
          {totalData.error_list.map((o: Record<string, any>, i: number) => (
            <li
              key={i}
              style={{
                color: 'var(--rs-progress-bar-fail)',
                fontSize: '0.85em'
              }}
            >{`${o.module || ''} [ ${o.error_code} ] ${o.error_desc}`}</li>
          ))}
          <li
            key={'overall_fail'}
            style={{
              color: 'var(--rs-progress-bar-fail)',
              fontSize: '0.85em'
            }}
          >
            {
              'Provisioning did not complete successfully. Our support team is investigating and will keep you informed.'
            }
          </li>
        </ul>
      ) : (
        <></>
      )}
      <Divider style={{ background: '#3a3a3a' }} />
      {socData?.map((o: Record<string, any>, i: number) => {
        return getServiceDetail(o, i, totalData?.bar_status === 'fail');
      })}
      <ButtonToolbar style={{ justifyContent: 'center' }}>
        <Button
          appearance="ghost"
          className="action-btn"
          onClick={() => {
            dispatch(
              globalApi.util.invalidateTags(['Accounts', 'Entitlements'])
            );
            dispatch(regionApi.util.invalidateTags(['RegionInstance']));
            // navigate(-1);
            // data?.arg?.setIsCheck(false);
            data?.arg?.callBack && data?.arg?.callBack();
          }}
        >
          Close
        </Button>
      </ButtonToolbar>
    </>
  );
};

const ProvisionSocStatus =
  (Component: FC<any>) =>
  ({ sn, task_id, account_id, region_alias, is_trial, callBack }: TaskObj) => {
    const { t } = useTranslation();
    const [region, setRegion] = useState<Record<string, any>>({});
    const [isCheck, setIsCheck] = useState<boolean>(
      !!(task_id || sn || is_trial)
    );
    const [data, setData] = useState<Record<string, any>[]>([]);
    const [overallData, setOverallData] = useState<Record<string, any>>({});
    const regions = useAppSelector(selectRegions);
    const { data: provisionStatus, error: provisionError } = useGetStatusQuery(
      {
        region: region,
        license_sn: sn,
        task_id: task_id,
        account_id: account_id || ''
      },
      {
        skip: !isCheck || !region?.token,
        pollingInterval: 10000, // 10 seconds
        refetchOnMountOrArgChange: true
      }
    );

    useEffect(() => {
      if (
        Object.keys(regions).length &&
        region_alias &&
        regions[region_alias]
      ) {
        setRegion(regions[region_alias]);
      }
    }, [regions, region_alias]);
    useEffect(() => {
      if (provisionStatus) {
        const resData: Record<string, any>[] = Object?.values(provisionStatus);
        const itemData = resData.filter((o) => typeof o === 'object');

        setData(itemData);
        setOverallData({
          status: provisionStatus?.status,
          error_code: provisionStatus?.error_code,
          error_desc: provisionStatus?.error_desc,
          stage: provisionStatus?.stage
        });
        const isAllDone =
          provisionStatus?.status === 'FAILURE' ||
          // both status : SUCCESS and stage : DONE
          // to be the complete status
          (provisionStatus?.status === 'SUCCESS' &&
            provisionStatus?.stage === 'DONE');
        //   ||
        // (itemData?.length &&
        //   itemData.every(
        //     (o: Record<string, any>) =>
        //       o?.status === 'SUCCESS' || o?.status === 'FAILURE'
        //   ));

        if (isAllDone) {
          // do refresh after provision done 5 seconds later,
          setIsCheck(false);
          if (provisionStatus?.stage === 'DONE') {
            setTimeout(() => {
              window && window.location?.reload();
            }, 5000);
          }
        }
      }
      if (provisionError) {
        setData([
          {
            module: 'Analyzer Service',
            status: 'FAILURE',
            error_desc: 'Get Provision Status Error'
          },
          {
            module: 'SOAR Service', //FSOC
            status: 'FAILURE',
            error_desc: 'Get Provision Status Error'
          },
          {
            module: 'SIEM Service',
            status: 'FAILURE',
            error_desc: 'Get Provision Status Error'
          }
        ]);
        setIsCheck(false);
      }
    }, [provisionStatus, t, provisionError]);
    return (
      <Component
        data={data}
        arg={{ task_id, region_alias, sn, setIsCheck, callBack }}
        over_all={overallData}
      />
    );
  };
export const ProvisionStatusButton = ProvisionSocStatus(CheckBtn);
export const ProvisionSocDetail = ProvisionSocStatus(SocProgressDetail);
